import React, { useEffect, useCallback } from 'react';
import sc from './Confirmation.module.scss';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import moment from 'moment';
import BSButton from 'Components/Button/BSButton';
import { HiOutlineExclamationCircle } from 'react-icons/hi';
import { useSelector, useDispatch } from 'react-redux';
import OnboardingHeader from 'Components/Pages/Onboarding/Header/Header';
import { removeRedundantDecimalPlaces } from 'helpers';
import { getCurrentSubscription } from 'services/subscriptionService';
import { actions } from 'redux/slices/auth';
import metaPixelService from 'services/metaPixelService';

const intervalMapping = {
  month: 'Monthly',
  year: 'Annual',
  lifetime: 'Lifetime',
};

const ConfirmSubscription = (summaryData) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { type, planName, price } = useParams();
  const { user } = useSelector((state) => state.auth);
  const { subscriptions } = user ?? {};
  const { current_subscription: currentSubscription } = subscriptions ?? {};
  const { end_date: endDate } = currentSubscription ?? {};
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const trial = queryParams.get('trial') || false;

  // Check if this is a lifetime plan
  const isLifetimePlan = type === 'lifetime' || summaryData?.summaryData?.interval === 'lifetime';

  const fetchCurrentSubscription = useCallback(async () => {
    try {
      const currentSubscriptionRes = await getCurrentSubscription();
      const { data: subscriptionData } = currentSubscriptionRes || {};
      const {
        current_subscription: currentSubscription,
        last_subscription: lastSubscription,
      } = subscriptionData || {};

      if (currentSubscription) {
        // Update Redux state with the new subscription data
        dispatch(
          actions.updateUser({
            user: {
              subscriptions: {
                current_subscription: currentSubscription,
                last_subscription: lastSubscription,
              },
            },
          }),
        );
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  }, [dispatch]);

  // Track trial start with Meta Pixel CAPI
  useEffect(() => {
    const trackTrialStart = async () => {
      // Only track if this is a trial start and we have user data
      if (trial && user?.email && metaPixelService.hasConsent()) {
        try {
          // Get price from params or summaryData
          const trialPrice = price || summaryData?.summaryData?.finalPrice;

          const result = await metaPixelService.trackStartTrial(
            user.email,
            user.phone || null,
            3, // 3-day trial
            trialPrice, // Pass the price for predicted_ltv
          );
        } catch (error) {
          console.error('Error tracking trial start:', error);
        }
      }
    };

    // Only track if we have user data
    if (user?.email) {
      trackTrialStart();
    }
  }, [trial, user?.email, user?.phone, price, summaryData]);

  useEffect(() => {
    // Fetch subscription data when component mounts
    fetchCurrentSubscription();
  }, [fetchCurrentSubscription]);

  return (
    <div className={sc.confrimation__root}>
      {(trial || location.pathname.startsWith('/resubscribe-confirmation')) && (
        <OnboardingHeader />
      )}

      {/* <div
        style={{ cursor: 'pointer', fontFamily: 'Inter' }}
        onClick={() => navigate(`/${BILLING_SUBSCRIPTION}`)}
      >
        <BSButton
          variant=""
          ButtonText="Back"
          classList="back-button"
          BtnWrapperClass="back-button"
          icon={<img src="/Images/arrowLeft.svg" alt="arrow" />}
        />
      </div> */}
      <div className={sc.thankYouWrapper}>
        <span>
          <img src="/Images/check-green.svg" alt="checkIcon" />
        </span>
        <h2 className={sc.heading}>Thank You!</h2>
        <p className={sc.desc}>
          You are now subscribed to our{' '}
          <strong>
            {isLifetimePlan
              ? (planName || summaryData?.summaryData?.planType)
              : `${intervalMapping[type] || intervalMapping[summaryData?.summaryData?.interval]} ${planName || summaryData?.summaryData?.planType}`
            }
          </strong>
          . We are thrilled to have you as a part of our community.
        </p>
        <div className={sc.outer_card}>
          <p>Subscription details</p>
          <span>Here's the summary of your subscription plan</span>
          <div className={sc.inner_card}>
            {/* <div className={sc.flex}>
              <p>Order Number</p>
              <span>#12345</span>
            </div> */}
            <div className={sc.flex}>
              <p>Plan - {planName || summaryData?.summaryData?.planType}</p>
              <span>One User</span>
            </div>
            {/* Hide billing cycle for lifetime plans */}
            {!isLifetimePlan && (
              <div className={sc.flex}>
                <p>Billing Cycle</p>
                <span>
                  {intervalMapping[type] ||
                    intervalMapping[summaryData?.summaryData?.interval]}
                </span>
              </div>
            )}
            <div className={sc.flex}>
              <p>{trial ? 'Total' : 'Total Amount Paid'}</p>
              <span>
                ${' '}
                {removeRedundantDecimalPlaces(
                  price || summaryData?.summaryData?.finalPrice,
                )}
                {!isLifetimePlan && `/${type || summaryData?.summaryData?.interval}`}
              </span>
            </div>
          </div>
        </div>
        {/* Show trial billing info OR hide next payment info for lifetime plans */}
        {trial ? (
          <div className={sc.thankYouWrapper__footer}>
            <HiOutlineExclamationCircle
              className={sc.thankYouWrapper__footer__icon}
            />
            <span>
              Your 3-day free trial has started! When your trial ends on{' '}
              {moment().add(3, 'days').format('MM/DD/YYYY')}, you will be
              automatically charged ${' '}
              {removeRedundantDecimalPlaces(
                price || summaryData?.summaryData?.finalPrice,
              )} for your{' '}
              {intervalMapping[type] ||
                intervalMapping[summaryData?.summaryData?.interval]}{' '}
              plan and billed every{' '}
              {type === 'month' || summaryData?.summaryData?.interval === 'month'
                ? 'month'
                : 'year'}{' '}
              thereafter until canceled.
            </span>
          </div>
        ) : (
          !isLifetimePlan && (
            <div className={sc.thankYouWrapper__footer}>
              <HiOutlineExclamationCircle
                className={sc.thankYouWrapper__footer__icon}
              />
              <span>
                The next payment will be charged at{' '}
                {moment(endDate ?? new Date())
                  .add(endDate ? 0 : 1, type === 'month' ? 'months' : 'years')
                  .format('MM/DD/YYYY')}
                .
              </span>
            </div>
          )
        )}
      </div>
      <div className={sc.button_section}>
        {/* <BSButton
          variant="dark"
          ButtonText="Get Receipt"
          classList="cancel_btn"
          BtnWrapperClass="back-button"
          icon={<img src="/Images/download.svg" alt="arrow" />}
        /> */}
        <BSButton
          variant=""
          ButtonText={
            currentSubscription === null || currentSubscription === undefined
              ? 'Please wait...'
              : 'Generate Content Plan'
          }
          classList="cancel_btn"
          BtnWrapperClass="back-button"
          iconPosition="right"
          onClick={() => {
            navigate('/');
          }}
          icon={
            currentSubscription !== null &&
            currentSubscription !== undefined && (
              <img src="/Images/chevron-right.svg" alt="arrow" />
            )
          }
          disabled={
            currentSubscription === null || currentSubscription === undefined
          }
        />
      </div>
    </div>
  );
};

export default ConfirmSubscription;